package com.evoapps.evoshift.ui.finance

import androidx.lifecycle.compose.collectAsStateWithLifecycle
import androidx.compose.runtime.key
import com.evoapps.evoshift.ui.theme.rememberLastNonNull
import com.evoapps.evoshift.ui.theme.motionContentSize
import com.evoapps.evoshift.ui.theme.MotionVisibility
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Warning
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.staticCompositionLocalOf
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.di.diViewModel
import com.evoapps.evoshift.domain.budget.PersonWithoutPay
import com.evoapps.evoshift.ui.DeepNavy
import com.evoapps.evoshift.ui.util.TestTags
import com.evoapps.evoshift.ui.util.asString
import com.evoapps.evoshift.viewmodels.MissingPayScope
import com.evoapps.evoshift.viewmodels.MissingPayUiState
import com.evoapps.evoshift.viewmodels.MissingPayViewModel
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.missing_pay_hint
import evoshift.composeapp.generated.resources.missing_pay_person
import evoshift.composeapp.generated.resources.missing_pay_set
import evoshift.composeapp.generated.resources.missing_pay_title
import evoshift.composeapp.generated.resources.missing_pay_unpaid_me
import evoshift.composeapp.generated.resources.missing_pay_unpaid_other
import org.jetbrains.compose.resources.pluralStringResource
import org.jetbrains.compose.resources.stringResource

/**
 * SH-147: opens [userId]'s pay terms at [workplaceId] (the employee form). Provided once by the
 * app's navigation; null (previews, tests without navigation) hides "Set pay".
 */
val LocalOpenPayTerms = staticCompositionLocalOf<((workplaceId: String, userId: String) -> Unit)?> { null }

private val WarningOrange = Color(0xFFE65100)
private val WarningBackground = Color(0xFFFFF3E0)
private val OnDarkWarning = Color(0xFFFFB74D)

/**
 * SH-147: "N people without pay set: labour cost is understated", naming them, on every screen
 * that shows labour cost ([scope]: its month, or one day). Each person gets "Set pay" (owners and
 * financial managers, [LocalOpenPayTerms]) and "Works without pay", stored once ("That's me – I
 * work without pay" on one's own entry). Nothing shows for roles without money, nor when everyone
 * has pay terms. [onDark]: on the roster's dark day details.
 */
@Composable
fun MissingPayBanner(scope: MissingPayScope, modifier: Modifier = Modifier, onDark: Boolean = false) {
    val viewModel = diViewModel<MissingPayScope, MissingPayViewModel>(scope)
    val state by viewModel.uiState.collectAsStateWithLifecycle()
    MissingPayContent(
        state = state,
        openPayTerms = LocalOpenPayTerms.current?.let { open -> { userId: String -> open(scope.workplaceId, userId) } },
        onWorksUnpaid = viewModel::confirmWorksUnpaid,
        modifier = modifier,
        onDark = onDark
    )
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
fun MissingPayContent(
    state: MissingPayUiState,
    openPayTerms: ((String) -> Unit)?,
    onWorksUnpaid: (String) -> Unit,
    modifier: Modifier = Modifier,
    onDark: Boolean = false
) {
    // SH-158: the banner unfolds when someone lacks pay and folds away once the last one has it
    // (still showing what it said while it goes); a person sorted out leaves the list smoothly.
    val lastShown = rememberLastNonNull(state.takeIf { it.shows })
    val text = if (onDark) Color.White else DeepNavy
    val accent = if (onDark) OnDarkWarning else WarningOrange
    MotionVisibility(state.shows, modifier) {
        val shown = lastShown ?: return@MotionVisibility
        Column(
            Modifier
                .fillMaxWidth()
                .motionContentSize()
                .clip(RoundedCornerShape(12.dp))
                .background(if (onDark) Color.White.copy(alpha = 0.08f) else WarningBackground)
                .testTag(TestTags.MISSING_PAY_BANNER)
                .padding(horizontal = 14.dp, vertical = 12.dp),
            verticalArrangement = Arrangement.spacedBy(6.dp)
        ) {
            val people = shown.people
            if (people.isNotEmpty()) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Icon(Icons.Default.Warning, contentDescription = null, tint = accent, modifier = Modifier.size(20.dp))
                    Spacer(Modifier.width(8.dp))
                    Text(
                        pluralStringResource(Res.plurals.missing_pay_title, people.size, people.size),
                        color = text,
                        fontWeight = FontWeight.Bold,
                        style = MaterialTheme.typography.bodyMedium
                    )
                }
                Text(stringResource(Res.string.missing_pay_hint), color = text.copy(alpha = 0.8f), style = MaterialTheme.typography.bodySmall)
                people.forEach { person ->
                    key(person.userId) { PersonRow(person, shown, openPayTerms, onWorksUnpaid, text, accent) }
                }
            }
            shown.error?.let { Text(it.asString(), color = if (onDark) OnDarkWarning else MaterialTheme.colorScheme.error, style = MaterialTheme.typography.bodySmall) }
        }
    }
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun PersonRow(
    person: PersonWithoutPay,
    state: MissingPayUiState,
    openPayTerms: ((String) -> Unit)?,
    onWorksUnpaid: (String) -> Unit,
    text: Color,
    accent: Color
) {
    Column(Modifier.fillMaxWidth().padding(top = 2.dp)) {
        Text(
            stringResource(Res.string.missing_pay_person, com.evoapps.evoshift.ui.util.personName(person.name), person.shifts, formatDecimal(person.hours, 1)),
            color = text,
            style = MaterialTheme.typography.bodyMedium
        )
        FlowRow(horizontalArrangement = Arrangement.spacedBy(4.dp)) {
            if (state.canSetPay && openPayTerms != null) {
                TextButton(onClick = { openPayTerms(person.userId) }) {
                    Text(stringResource(Res.string.missing_pay_set), color = accent, fontWeight = FontWeight.Bold)
                }
            }
            if (state.canConfirmUnpaid) {
                val mine = person.userId == state.currentUserId
                TextButton(onClick = { onWorksUnpaid(person.userId) }, enabled = person.userId !in state.confirming) {
                    Text(
                        stringResource(if (mine) Res.string.missing_pay_unpaid_me else Res.string.missing_pay_unpaid_other),
                        color = text.copy(alpha = 0.85f)
                    )
                }
            }
        }
    }
}
